import type { FC, PropsWithChildren } from 'react'
import { useEffect, useRef } from 'react'
import type { ChannelListMessengerProps, DefaultStreamChatGenerics } from 'stream-chat-react'
import { useChannelListContext } from 'stream-chat-react'

import { useChatClient } from 'src/features/chats/context/chat-context'

import ChatListSkeleton from '../../skeletons/chat-list-skeleton'
import ChatPreviewSkeleton from '../../skeletons/chat-preview-skeleton'
import useChatStore from '../../store/chat-store'
import ChatLoadingErrorMessage from '../ui/chat-loading-error-message'

type StreamChannelListProps = PropsWithChildren<ChannelListMessengerProps<DefaultStreamChatGenerics>> & {
  onEndReached?: () => void
  isLoadingMore?: boolean
}

export const StreamChannelList: FC<StreamChannelListProps> = ({
  children,
  loading,
  error,
  onEndReached,
  isLoadingMore = false,
}) => {
  const { channels: loadedChannels } = useChannelListContext()
  const { client, isConnecting, reconnect } = useChatClient()
  const endRef = useRef<HTMLDivElement | null>(null)
  const conversationId = useChatStore((state) => state.conversationId)
  const setConversationId = useChatStore((state) => state.setConversationId)

  const isLoading = loading || isConnecting || !client

  const handleRetry = async () => {
    try {
      await reconnect()
      window.location.reload()
    } catch (error) {
      console.error('Reconnection failed:', error)
    }
  }

  useEffect(() => {
    if (conversationId || loadedChannels.length === 0) return
    const firstChannel = loadedChannels[0]
    if (firstChannel?.cid) {
      setConversationId(firstChannel.cid)
    }
  }, [conversationId, loadedChannels, setConversationId])

  useEffect(() => {
    if (!onEndReached || !endRef.current) return

    const target = endRef.current
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting) {
          onEndReached()
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 },
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [onEndReached, loadedChannels.length])

  if (isLoading) {
    return <ChatListSkeleton />
  }

  if (error) {
    return <ChatLoadingErrorMessage onRetry={handleRetry} />
  }

  if (loadedChannels.length === 0) {
    return null
    // return <EmptyState />
  }

  return (
    <div className="flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {children}
        {!!isLoadingMore && (
          <div className="py-0">
            <ChatPreviewSkeleton />
            <ChatPreviewSkeleton />
            <ChatPreviewSkeleton />
            <ChatPreviewSkeleton />
          </div>
        )}
        <div ref={endRef} className="h-1 w-full" />
      </div>
    </div>
  )
}
